/**
 * ClubhouseNav Component
 * Secondary navigation for Member Clubhouse
 * Supports desktop persistent nav and mobile drawer
 */
import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { 
  Trophy, 
  Users, 
  Landmark, 
  LogOut, 
  ArrowLeft,
  Menu,
  X,
  Home,
  User
} from 'lucide-react';
import './ClubhouseNav.css';

export const ClubhouseNav = ({ onLogout, userProfile }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [profileDropdownOpen, setProfileDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setProfileDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const navItems = [
    { 
      id: 'dashboard',
      label: 'Dashboard', 
      icon: Home, 
      path: '/clubhouse/dashboard',
      onClick: () => navigate('/clubhouse/dashboard')
    },
    { 
      id: 'leaderboards',
      label: 'Leaderboards', 
      icon: Trophy, 
      path: '/clubhouse/leaderboards',
      onClick: () => navigate('/clubhouse/leaderboards')
    },
    { 
      id: 'members',
      label: 'Members', 
      icon: Users, 
      path: '/clubhouse/members',
      onClick: () => navigate('/clubhouse/members')
    },
    { 
      id: 'vault',
      label: 'Member Vault', 
      icon: Landmark, 
      path: '/clubhouse/locker',
      onClick: () => navigate('/clubhouse/locker')
    }
  ];

  const isActive = (path) => {
    if (path === '/clubhouse/dashboard') {
      return location.pathname === '/clubhouse/dashboard' || location.pathname === '/clubhouse';
    }
    return location.pathname.startsWith(path);
  };

  const handleNavClick = (item) => {
    item.onClick();
    setMobileMenuOpen(false);
  };

  const API_URL = process.env.REACT_APP_BACKEND_URL || '';

  return (
    <>
      {/* Desktop Navigation */}
      <nav className="clubhouse-nav desktop-nav">
        <div className="nav-container">
          {/* Main Nav Items */}
          <div className="nav-items">
            {navItems.map((item) => (
              <button
                key={item.id}
                className={`nav-item ${isActive(item.path) ? 'active' : ''} ${item.highlight ? 'highlight' : ''}`}
                onClick={() => handleNavClick(item)}
              >
                <item.icon size={18} />
                <span>{item.label}</span>
              </button>
            ))}
          </div>

          {/* Profile Photo with Dropdown */}
          <div className="nav-profile-container" ref={dropdownRef}>
            <button 
              className="nav-profile-btn"
              onClick={() => setProfileDropdownOpen(!profileDropdownOpen)}
              aria-label="Profile menu"
            >
              {userProfile?.profile_photo_url ? (
                <img 
                  src={`${API_URL}${userProfile.profile_photo_url}`} 
                  alt="Profile" 
                  className="nav-profile-photo"
                />
              ) : (
                <div className="nav-profile-placeholder">
                  <User size={20} />
                </div>
              )}
            </button>

            {/* Profile Dropdown */}
            <div className={`nav-profile-dropdown ${profileDropdownOpen ? 'open' : ''}`}>
              <button 
                className="dropdown-item"
                onClick={() => {
                  navigate('/');
                  setProfileDropdownOpen(false);
                }}
              >
                <ArrowLeft size={16} />
                <span>Leave Clubhouse</span>
              </button>
              <button 
                className="dropdown-item logout"
                onClick={() => {
                  onLogout();
                  setProfileDropdownOpen(false);
                }}
              >
                <LogOut size={16} />
                <span>Logout</span>
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Mobile Navigation */}
      <nav className="clubhouse-nav mobile-nav">
        <div className="mobile-nav-header">
          <button 
            className="mobile-menu-toggle"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-label="Toggle navigation"
          >
            {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
          <span className="mobile-nav-title">Clubhouse</span>
          
          {/* Mobile Profile Photo */}
          <button 
            className="mobile-profile-btn"
            onClick={() => setProfileDropdownOpen(!profileDropdownOpen)}
          >
            {userProfile?.profile_photo_url ? (
              <img 
                src={`${API_URL}${userProfile.profile_photo_url}`} 
                alt="Profile" 
                className="mobile-profile-photo"
              />
            ) : (
              <div className="mobile-profile-placeholder">
                <User size={18} />
              </div>
            )}
          </button>
        </div>

        {/* Mobile Menu Drawer */}
        <div className={`mobile-nav-drawer ${mobileMenuOpen ? 'open' : ''}`}>
          <div className="mobile-nav-items">
            {navItems.map((item) => (
              <button
                key={item.id}
                className={`mobile-nav-item ${isActive(item.path) ? 'active' : ''} ${item.highlight ? 'highlight' : ''}`}
                onClick={() => handleNavClick(item)}
              >
                <item.icon size={20} />
                <span>{item.label}</span>
              </button>
            ))}
          </div>
          
          <div className="mobile-nav-divider" />
          
          <div className="mobile-nav-utils">
            <button 
              className="mobile-nav-item"
              onClick={() => {
                navigate('/');
                setMobileMenuOpen(false);
              }}
            >
              <ArrowLeft size={20} />
              <span>Leave Clubhouse</span>
            </button>
            <button 
              className="mobile-nav-item logout"
              onClick={() => {
                onLogout();
                setMobileMenuOpen(false);
              }}
            >
              <LogOut size={20} />
              <span>Logout</span>
            </button>
          </div>
        </div>

        {/* Overlay */}
        {mobileMenuOpen && (
          <div 
            className="mobile-nav-overlay"
            onClick={() => setMobileMenuOpen(false)}
          />
        )}
      </nav>
    </>
  );
};

export default ClubhouseNav;
